import { type GateState, type IctStanding } from "../api/client";
import { Callout } from "../components/Callout";

/**
 * The MD 4 gates, and what each one stops.
 *
 * Because ICT explicitly includes AI, these attach to every audit on the platform rather
 * than only to remote video. A Stage 1 conducted remotely by an AI is the most
 * ICT-dependent case there is, not an exception to the mandatory document.
 *
 * **A blocking gate that is unanswered blocks exactly as a refused one does**, and the two
 * are drawn apart because the remedy differs: one needs asking, the other needs a different
 * conduct mode. That is why `satisfied` is three-valued and the standing column says "Not
 * recorded" rather than "No" — an unasked question and a negative answer are different
 * facts about the audit, and only the second is an explanation.
 *
 * **The card appears at more than one step because MD 4 stages the gates.** Cl. 4.2.2 is
 * confirmed at application review and Cl. 4.2.7 at scope, so both are due before the
 * determination is confirmed; competency (Cl. 4.2.4) attests to a technology the plan is
 * what names, so it is asked on the plan; and mutual agreement (Cl. 4.1.2) is the
 * auditee's to give in their own name, so it is asked on the access-link landing. A
 * card that asserted all six at every step would refuse Step 2 for want of an answer
 * nobody is yet in a position to give, so each caller names the step it is gating and the
 * server owns the mapping.
 */
/**
 * `SCOPE_CONFIRMATION_GATES` in `api/ict.py`, mirrored so Step 2 can list the gates it is
 * waiting on. The server stays the authority — it returns `blocks_scope_confirmation` and
 * refuses the confirmation itself, and this list only decides which rows are drawn.
 */
export const SCOPE_CONFIRMATION_GATES = [
  "infrastructure_check",
  "virtual_site_declaration",
] as const;

/**
 * `PLAN_ISSUE_GATES` in `api/ict.py`, mirrored for Step 3. The competency attestation
 * (Cl. 4.2.4) becomes answerable here and nowhere earlier — the plan is what names the
 * technology in use — and the two Step 2 gates stay on the table because plan issue is
 * assessed against all three: a gate flipped after the Step 2 approval blocks the issue,
 * and the answer to it has to be reachable from the screen the refusal is read on.
 */
export const PLAN_ISSUE_GATES = [
  ...SCOPE_CONFIRMATION_GATES,
  "competency_attestation",
] as const;

export function IctGatesCard({
  standing,
  onRecord,
  step,
  only,
}: {
  standing: IctStanding;
  onRecord: (gate: string, satisfied: boolean) => void;
  /** Which step's block list to read, and which refusal copy to show. */
  step: "scope_confirmation" | "plan_issue" | "session_start";
  /**
   * Restrict the table to these gates. Step 2 passes the two it is waiting on rather than
   * listing all six, because a gate shown with buttons on a screen where it is not yet
   * answerable invites an answer given too early to mean anything.
   */
  only?: readonly string[];
}) {
  const outstanding =
    step === "scope_confirmation"
      ? standing.blocks_scope_confirmation
      : step === "plan_issue"
        ? standing.blocks_plan_issue
        : standing.blocks_session_start;
  const rows = only
    ? standing.gates.filter((gate) => only.includes(gate.gate))
    : standing.gates;

  return (
    <div className="card">
      <h2>IAF MD 4 — information and communication technology</h2>
      <p className="sub" style={{ marginBottom: "12px" }}>
        ICT explicitly includes AI, so these gates attach to this audit whether or not any
        video is used. {standing.versioning_rule}
      </p>

      {outstanding.length ? (
        <Callout
          tone="block"
          title={
            step === "scope_confirmation"
              ? "The scope and duration cannot be confirmed."
              : step === "plan_issue"
                ? "The plan cannot be approved and issued."
                : "The session cannot begin."
          }
        >
          {outstanding.map((gate) => label(gate)).join(", ")} outstanding.{" "}
          {standing.alternative_conduct}
        </Callout>
      ) : (
        <Callout tone="info" title="Every blocking gate is satisfied.">
          {step === "scope_confirmation"
            ? "Nothing in MD 4 is holding the determination. The later gates are asked at plan issue and on audit day."
            : step === "plan_issue"
              ? "Nothing in MD 4 is holding the plan. Mutual agreement (Cl. 4.1.2) remains, and the auditee gives it in their own name before the session opens."
              : "The audit may be conducted using the agreed ICT."}
        </Callout>
      )}

      <div className="tscroll">
        <table>
          <thead>
            <tr>
              <th>Gate</th>
              <th>Clause</th>
              <th>Requirement</th>
              <th>Standing</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((gate) => (
              <Gate key={gate.gate} gate={gate} onRecord={onRecord} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Gate({
  gate,
  onRecord,
}: {
  gate: GateState;
  onRecord: (gate: string, satisfied: boolean) => void;
}) {
  return (
    <tr>
      <td>
        <b>{label(gate.gate)}</b>
        {gate.blocking ? (
          <>
            <br />
            <span className="sub">Blocking</span>
          </>
        ) : null}
      </td>
      <td className="mono">Cl. {gate.clause}</td>
      <td className="sub">{gate.title}</td>
      <td>
        {gate.satisfied === null ? (
          <span className="pill p-mute">Not recorded</span>
        ) : gate.satisfied ? (
          <span className="pill p-ok">Satisfied</span>
        ) : (
          <span className="pill p-red">Not satisfied</span>
        )}
        {gate.recorded_by ? (
          <div className="sub" style={{ fontSize: "11px" }}>
            {gate.recorded_by} · {stamp(gate.recorded_at)}
          </div>
        ) : null}
      </td>
      <td style={{ whiteSpace: "nowrap" }}>
        {/* The mutual agreement gate is the auditee's to give, in their own name — the
            certification body recording it here would be asserting consent on the client's
            behalf, which is what Cl. 4.1.2 asks for the second time. */}
        {gate.gate === "mutual_agreement" ? (
          <span className="sub">Given by the auditee</span>
        ) : (
          <>
            <button
              type="button"
              className="btn sec sm"
              onClick={() => onRecord(gate.gate, true)}
            >
              Satisfied
            </button>{" "}
            <button
              type="button"
              className="btn sec sm"
              onClick={() => onRecord(gate.gate, false)}
            >
              Not satisfied
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

function label(value: string): string {
  return value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

function stamp(value: string | null | undefined): string {
  return value ? value.slice(0, 16).replace("T", " ") : "—";
}
